import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Lock, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { supabase, offerStartsAt, offerCurrentPrice, offerOriginalPrice } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const { id } = useParams();
  const { authUser } = useAuth();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState(authUser?.email || "");

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("offers")
        .select("*, business:businesses(*)")
        .eq("id", id)
        .maybeSingle();
      setOffer(data);
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => { if (authUser?.email) setEmail(authUser.email); }, [authUser]);

  const goToStripe = async (e) => {
    e.preventDefault();
    if (!authUser && !email) { toast.error("Introduce tu email"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offer_id: id,
          email_guest: authUser ? null : email,
          user_id: authUser?.id ?? null,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data?.detail || "No se pudo iniciar el pago");
      }
      // Stash booking id locally — used if the user closes the tab.
      try { sessionStorage.setItem(`slotfy:lastBooking:${data.session_id}`, data.booking_id); } catch {}
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.message || "Error al iniciar el pago");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-sage-deep border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!offer) return <div className="text-center py-24 text-stone">Actividad no encontrada</div>;

  const cur = offerCurrentPrice(offer);
  const orig = offerOriginalPrice(offer);

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12 md:py-20" data-testid="checkout-page">
      <Link to={`/actividad/${id}`} className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-stone hover:text-sage-deep transition mb-6">
        <ArrowLeft size={14} /> Volver
      </Link>
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10">
        <form onSubmit={goToStripe} className="space-y-8" data-testid="checkout-form">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-light text-forest mb-2">Completa tu reserva</h1>
            <p className="text-stone text-sm">Pago seguro con Stripe. Recibirás el QR por email al confirmar.</p>
          </div>

          <section className="space-y-4">
            <h2 className="text-xs uppercase tracking-[0.3em] text-sage-deep">/ Tu email</h2>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-stone block mb-2">Email</span>
              <input
                data-testid="checkout-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!authUser}
                placeholder="tu@email.com"
                className="w-full bg-white border border-forest/15 px-4 py-3 text-sm focus:border-sage-deep focus:outline-none transition disabled:opacity-50 rounded-md"
              />
            </label>
          </section>

          <section className="space-y-3 rounded-2xl border border-sage/30 bg-white p-5">
            <div className="flex items-center gap-3 text-sage-deep">
              <ShieldCheck size={20} weight="duotone" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">Pago seguro con Stripe</span>
            </div>
            <p className="text-stone text-sm">
              Al continuar serás redirigido a la página de pago de Stripe para introducir tu tarjeta de forma segura. Slotfy nunca guarda los datos de tu tarjeta.
            </p>
            <div className="text-[11px] text-ash">
              Para probar en modo demo usa: <span className="font-mono bg-mist px-1.5 py-0.5 rounded">4242 4242 4242 4242</span>, cualquier fecha futura y CVC.
            </div>
          </section>

          <button
            type="submit"
            disabled={submitting}
            data-testid="confirm-booking-btn"
            className="w-full bg-sage-deep text-paper py-4 font-medium tracking-wide text-sm hover:bg-forest transition disabled:opacity-50 rounded-full inline-flex items-center justify-center gap-2"
          >
            <CreditCard size={18} />
            {submitting ? "Redirigiendo a Stripe…" : `Pagar ${cur.toFixed(2)}€ con Stripe`}
            <Lock size={14} />
          </button>
        </form>

        <aside className="lg:sticky lg:top-24 self-start">
          <div className="border border-forest/10 bg-white rounded-2xl p-6 space-y-4 shadow-[0_8px_32px_-12px_rgba(44,62,45,0.10)]">
            <div className="text-xs uppercase tracking-[0.2em] text-stone">Tu reserva</div>
            {(offer.image_url || offer.business?.image_url) && (
              <img src={offer.image_url || offer.business?.image_url} className="w-full aspect-video object-cover rounded-xl" alt="" />
            )}
            <div>
              <div className="font-heading text-xl font-semibold text-forest">{offer.title}</div>
              <div className="text-sm text-stone">{offer.business?.name}</div>
              {offerStartsAt(offer) && (
                <div className="text-xs text-stone mt-1">
                  {offerStartsAt(offer).toLocaleString("es-ES", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </div>
            <div className="border-t border-forest/10 pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-stone">Subtotal</span><span className="line-through text-ash">{orig.toFixed(2)}€</span></div>
              <div className="flex justify-between"><span className="text-sage-deep">Descuento</span><span className="text-sage-deep">-{(orig - cur).toFixed(2)}€</span></div>
              <div className="flex justify-between font-heading text-xl font-semibold pt-3 border-t border-forest/10"><span className="text-forest">Total</span><span className="text-sage-deep">{cur.toFixed(2)}€</span></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
